import { UlmContainer } from "@components/ulm/ulm-container";
import { Box } from "@mui-wrapper";
import { QueryParams } from "@router/route-params";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { EmailVerifiedRedirection } from "@components/verify-email-page/email-verified-redirection";
import { EmailVerificationError } from "@components/verify-email-page/email-verification-error";
import { VerifyingEmail } from "@components/verify-email-page/verifying-email";
import { PageTitle } from "@components/common/page-title";
import { HttpUtils } from "@stellar/utils";
import { faroCoreApiClient } from "@api/core-api/core-api-utils";

/** Renders component to verify the email after a successful sign-up */
export function VerifyEmail(): JSX.Element {
  const [searchParams] = useSearchParams();
  const { handleError } = useErrorHandlerContext();

  /**
   * Defines the content to show in the page, either a message indicating
   * that the email is being verified, an error message, or a success message
   * with a redirection to the dashboard.
   */
  const [content, setContent] = useState<ReactNode>(<VerifyingEmail />);

  const verifyEmail = useCallback(async () => {
    const verifyKey = searchParams.get(QueryParams.key);

    // Early exit the function and show an error toast if the verification key is undefined
    if (!verifyKey) {
      setContent(<EmailVerificationError />);
      return;
    }

    try {
      await faroCoreApiClient.users.verifyEmail({ verifyKey });
      setContent(<EmailVerifiedRedirection />);
    } catch (error) {
      // If the error status code is 404 it just means that the email verification key is not valid.
      // So only send error to Sentry if the error is not a 404.
      const errorStatus = HttpUtils.getErrorStatus(error);
      const isInvalidKeyError =
        errorStatus !== undefined &&
        errorStatus === HttpUtils.HTTP_ERROR_CODE.NotFound;
      if (!isInvalidKeyError) {
        handleError({
          id: `verifyEmail-${Date.now().toString()}`,
          title: "Failed to verify the email address.",
          error,
          uiType: "none",
        });
      }

      setContent(<EmailVerificationError />);
    }
  }, [handleError, searchParams]);

  /** Verifies the email only once on page load */
  useEffect(() => {
    void verifyEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- request email verification only once
  }, []);

  return (
    <UlmContainer>
      <PageTitle>Email Address Verification</PageTitle>

      <Box
        sx={{
          marginTop: "40px",
          fontSize: "14px",
          color: "text.primary",
        }}
      >
        {content}
      </Box>
    </UlmContainer>
  );
}
